import React, { useEffect, useState } from 'react';
import type { ReviewCycle, ReviewerRef } from './types';
import { reviewersWithoutStep } from './reviewStage';
import { fetchContexts } from './useCurrentUserStep';

interface Props {
  cycle: ReviewCycle;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

/**
 * An assigned reviewer who contributes no step — said out loud, where the
 * decision maker is looking (2026-08-31).
 *
 * Found on a live cycle: the legal reviewer was assigned, had commented four
 * times and filed an assessment, and the sequence read medical → producer.
 * deriveReviewSequence builds steps from ReviewerContext domains, and he had
 * none — so no step waited on him, the STEPS_NOT_COMPLETE gate had nothing to
 * hold, and nothing anywhere said so. The permission bit and the context
 * answer different questions, and only people who read the code knew that.
 *
 * Renders nothing when every assigned reviewer resolves a domain, and nothing
 * on a contexts fetch failure — this is a diagnostic, not a gate, and a
 * network blip must not paint a false warning.
 */
export default function MissingStepWarning({ cycle, getAuthHeaders }: Props) {
  const [missing, setMissing] = useState<ReviewerRef[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const contexts = await fetchContexts(getAuthHeaders);
      if (cancelled) return;
      // An empty list is indistinguishable from a failed fetch upstream
      // (fetchContexts returns [] on error) — in both cases stay quiet rather
      // than accuse every reviewer at once on a blip.
      if (contexts.length === 0) { setMissing([]); return; }
      setMissing(reviewersWithoutStep(cycle, contexts));
    })();
    return () => { cancelled = true; };
  }, [cycle, getAuthHeaders]);

  if (missing.length === 0) return null;

  return (
    <div
      data-testid="missing-step-warning"
      style={{ padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: 8, margin: '8px 0', fontSize: 13, color: '#664d03' }}
    >
      <strong>
        {missing.map(r => r.name || r.email).join(', ')}{' '}
        {missing.length === 1 ? 'is' : 'are'} assigned but {missing.length === 1 ? 'has' : 'have'} no reviewer domain.
      </strong>
      <p style={{ margin: '4px 0 0' }}>
        The review sequence is built from reviewer domains, so <em>no step will wait on them</em> —
        they can comment and assess, but approval will not be held for their review. Set their
        domain under <strong>Reviewer Contexts</strong>, then re-save this cycle&rsquo;s reviewers
        so the sequence is re-derived.
      </p>
    </div>
  );
}
